import type { AdminPlan, FeatureKey, LimitKey, PlanFeatures, PlanLimits } from '@/lib/api/types';

/**
 * The plan editor's model and its validation — pure, so it can be reasoned
 * about without the dialog around it.
 *
 * Every numeric field is held as a STRING: a half-typed number is not a
 * number, and a form that coerces on every keystroke fights the person
 * typing. Prices are validated as text too — `Number.isInteger(x * 100)` is
 * the binary-float trap that rejects 29.90.
 */
export const LIMIT_KEYS: LimitKey[] = ['hotels', 'rooms', 'users', 'channels'];
export const FEATURE_KEYS: FeatureKey[] = ['bookingEngine', 'directApi', 'fullReports', 'publicApi'];

export const LIMIT_LABEL: Record<LimitKey, string> = { hotels: 'Отели', rooms: 'Номера', users: 'Пользователи', channels: 'Каналы' };

export const FEATURE_LABEL: Record<FeatureKey, string> = {
  bookingEngine: 'Модуль бронирования',
  directApi:     'Прямые API каналов',
  fullReports:   'Полные отчёты',
  publicApi:     'Публичный API',
};

export const FEATURE_HINT: Record<FeatureKey, string> = {
  bookingEngine: 'Собственная страница бронирования отеля',
  directApi:     'Подключение OTA напрямую, без channel manager',
  fullReports:   'Все отчёты и статистика, а не только базовые',
  publicApi:     'Доступ к API платформы по ключу организации',
};

/** Digits with at most two decimals — the backend's `Decimal(12, 2)` column. */
const PRICE_RE = /^\d+(\.\d{1,2})?$/;
/** CreatePlanDto: `^[a-z][a-z0-9_-]*$`, 2–32 characters. */
const CODE_RE = /^[a-z][a-z0-9_-]{1,31}$/;

export interface PlanForm {
  code: string;
  name: string;
  currency: string;
  priceMonthly: string;
  priceYearly: string;
  isActive: boolean;
  /** '' = unlimited (the backend's `null`). */
  limits: Record<LimitKey, string>;
  features: Record<FeatureKey, boolean>;
}

export const BLANK_FORM: PlanForm = {
  code: '',
  name: '',
  currency: 'UZS',
  priceMonthly: '0',
  priceYearly: '0',
  isActive: true,
  limits: { hotels: '1', rooms: '10', users: '2', channels: '1' },
  features: { bookingEngine: false, directApi: false, fullReports: false, publicApi: false },
};

export function toForm(plan: AdminPlan): PlanForm {
  const limit = (v: number | null) => (v === null ? '' : String(v));
  return {
    code: plan.code,
    name: plan.name,
    currency: plan.currency,
    priceMonthly: String(plan.priceMonthly),
    priceYearly: String(plan.priceYearly),
    isActive: plan.isActive,
    limits: { hotels: limit(plan.limits.hotels), rooms: limit(plan.limits.rooms), users: limit(plan.limits.users), channels: limit(plan.limits.channels) },
    features: { ...plan.features },
  };
}

/** "Pro Plus 2027" → "pro-plus-2027": a code suggestion for a new plan, transliterating Cyrillic. */
export function suggestCode(name: string): string {
  const map: Record<string, string> = { а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'c', ч: 'ch', ш: 'sh', щ: 'sch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya' };
  return name.toLowerCase().split('').map((ch) => map[ch] ?? ch).join('')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').replace(/^[^a-z]+/, '').slice(0, 32);
}

/** How much cheaper the year is than twelve months, in percent; null when either price is zero. */
export function yearlyDiscount(monthly: number, yearly: number): number | null {
  if (!monthly || !yearly) return null;
  return Math.round((1 - yearly / (monthly * 12)) * 100);
}

export interface ValidPlan {
  code: string;
  name: string;
  currency: string;
  priceMonthly: number;
  priceYearly: number;
  isActive: boolean;
  limits: PlanLimits;
  features: PlanFeatures;
}

export type Validation = { ok: true; value: ValidPlan } | { ok: false; error: string };

/** Mirrors the backend Zod schema so a typo is caught before the round trip. */
export function validate(form: PlanForm, isCreate: boolean): Validation {
  const name = form.name.trim();
  if (!name) return { ok: false, error: 'Введите название' };
  if (name.length > 120) return { ok: false, error: 'Название: не длиннее 120 символов' };
  const code = form.code.trim();
  if (isCreate && !CODE_RE.test(code)) return { ok: false, error: 'Код: латиница в нижнем регистре, 2–32 символа, начинается с буквы' };
  const currency = form.currency.trim().toUpperCase();
  if (!/^[A-Z]{3}$/.test(currency)) return { ok: false, error: 'Валюта: 3 буквы, например UZS' };
  const monthly = form.priceMonthly.trim();
  const yearly = form.priceYearly.trim();
  if (!PRICE_RE.test(monthly) || !PRICE_RE.test(yearly)) return { ok: false, error: 'Цена: число, максимум 2 знака после точки' };
  const limits: PlanLimits = { hotels: null, rooms: null, users: null, channels: null };
  for (const key of LIMIT_KEYS) {
    const raw = form.limits[key].trim();
    if (raw === '') continue;
    const n = Number(raw);
    if (!Number.isInteger(n) || n <= 0) return { ok: false, error: `${LIMIT_LABEL[key]}: целое число больше нуля или пусто` };
    limits[key] = n;
  }
  return { ok: true, value: { code, name, currency, priceMonthly: Number(monthly), priceYearly: Number(yearly), isActive: form.isActive, limits, features: { ...form.features } } };
}
